<template>
   <div class="detail-card">
      <!-- ── Header ── -->

      <div class="detail-header">
         <div class="header-left">
            <div class="group-page-header">
               <h1 class="group-page-title">{{ $t("taskLogs_title") }}</h1>
               <p class="group-page-subtitle">
                  {{ $t("taskLogs_createdAt") }} <span v-if="task">{{ task.openedDatetime | FormatterDate }}</span>
                  <span v-else-if="loading" class="skeleton skeleton--sm"></span>
               </p>
            </div>
         </div>
         <div v-if="task" class="gts-badge" :class="'ETaskSubStatus-' + task.subStatus">{{ statusLabel() }}</div>
         <div v-else-if="loading" class="skeleton skeleton--badge"></div>
      </div>

      <!-- ── Info Cards ── -->
      <div class="info-cards">
         <template v-if="task && task.groupPnrDetails">
            <div class="info-item">
               <p class="info-label">{{ $t("taskLogs_requestId") }}</p>
               <p class="info-value">{{ task.id }}</p>
            </div>
            <div class="info-separator"></div>
            <div class="info-item">
               <p class="info-label">{{ $t("taskLogs_route") }}</p>
               <p class="info-value">{{ task.groupPnrDetails.departureAirport }} - {{ task.groupPnrDetails.arrivalAirport }}</p>
            </div>
            <div class="info-separator"></div>
            <div class="info-item">
               <p class="info-label">{{ $t("taskLogs_date") }}</p>
               <p class="info-value">{{ task.groupPnrDetails.departureDate | FormatterDate }} - {{ task.groupPnrDetails.returnDate | FormatterDate }}</p>
            </div>
            <div class="info-separator"></div>
            <div class="info-item">
               <p class="info-label">{{ $t("taskLogs_passengerCount") }}</p>
               <p class="info-value">A: {{ task.groupPnrDetails.paxCounts.ADULT }}, C: {{ task.groupPnrDetails.paxCounts.CHILD }}, I: {{ task.groupPnrDetails.paxCounts.INFANT }}</p>
            </div>
            <div class="info-separator"></div>
            <div class="info-item">
               <p class="info-label">{{ $t("taskLogs_cabinClass") }}</p>
               <p class="info-value">{{ task.groupPnrDetails.cabinType | EFltCabinType }}</p>
            </div>
            <div class="info-separator"></div>
            <div class="info-item">
               <p class="info-label">{{ $t("taskRequestList_lastTicketDateTime") }}</p>
               <p class="info-value" :class="task.groupPnrDetails.lastTicketDateTime ? 'last-ticket-date-time' : ''">
                  {{ task.groupPnrDetails.lastTicketDateTime ? (task.groupPnrDetails.lastTicketDateTime | FormatterDD_MM_YYYY_HH_mm) : '-' }}
               </p>
            </div>
         </template>
         <template v-else-if="loading">
            <template v-for="n in 7">
               <div :key="'item-' + n" class="info-item">
                  <div class="skeleton skeleton--xs"></div>
                  <div class="skeleton skeleton--sm" style="margin-top: 4px"></div>
               </div>
               <div v-if="n < 7" :key="'sep-' + n" class="info-separator"></div>
            </template>
         </template>
      </div>

      <!-- ── Error ── -->
      <div v-if="error" class="error-state">{{ $t("taskLogs_errorLoading") }}</div>

      <!-- ── Timeline Mobile ── -->
      <div class="timeline-mobile">
         <div v-for="(step, i) in timelineSteps" :key="i" class="timeline-step">
            <div class="step-indicator" :class="'step-indicator--' + step.state">
               <svg v-if="step.state === 'done'" class="check-svg" viewBox="0 0 10 10" fill="none">
                  <path d="M2 5L4 7L8 3" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
               </svg>
               <span v-else-if="step.state === 'active'" class="active-dot"></span>
            </div>
            <div class="step-info">
               <p class="step-label" :class="'step-label--' + step.state">{{ step.label }}</p>
               <p v-if="step.date" class="step-date">{{ step.date }}</p>
            </div>
         </div>
      </div>

      <!-- ── Content Tabs ── -->
      <div v-if="task && task.groupPnrDetails && (task.groupPnrDetails.paxEntryEnabled || hasExistingPassengers)" class="content-tabs">
         <button class="tab-button" :class="{ active: activeContentTab === 'passengers' }" @click="activeContentTab = 'passengers'">
            {{ $t("taskLogs_passengerList") }}
         </button>
         <button class="tab-button" :class="{ active: activeContentTab === 'chat' }" @click="activeContentTab = 'chat'">{{ $t("taskLogs_chat") }}</button>
      </div>

      <!-- ── Passenger Entry Form ── -->
      <div v-if="activeContentTab === 'passengers' && passengers.length > 0" class="passenger-entry-section" :class="{ 'table-view': isPassengersReadOnly }">
         <div class="passenger-entry-header">
            <div class="passenger-entry-header-content" v-if="!isPassengersReadOnly">
               <h2 class="section-title">{{ $t("taskLogs_passengerInfo") }}</h2>
               <p class="section-subtitle">{{ $t("taskLogs_passengerInfoDesc") }}</p>
            </div>
         </div>
         <div>
            <!-- Read-only table view for existing passengers -->
            <div v-if="isPassengersReadOnly" class="passenger-table">
               <div class="passenger-table-header">
                  <div class="table-header-cell">#</div>
                  <div class="table-header-cell">{{ $t("common_type") }}</div>
                  <div class="table-header-cell">{{ $t("common_gender") }}</div>
                  <div class="table-header-cell">{{ $t("taskLogs_firstName") }}</div>
                  <div class="table-header-cell">{{ $t("taskLogs_lastName") }}</div>
                  <div class="table-header-cell">{{ $t("taskLogs_birthdate") }}</div>
               </div>
               <div class="passenger-table-body">
                  <div v-for="(passenger, index) in passengers" :key="index" class="passenger-table-row">
                     <div class="table-cell">{{ index + 1 }}</div>
                     <div class="table-cell">
                        <span class="type-badge">{{ passenger.type | EFltPaxType }}</span>
                     </div>
                     <div class="table-cell">{{ passenger.gender === 1 ? $t("common_male") : $t("common_female") }}</div>
                     <div class="table-cell">{{ passenger.name }}</div>
                     <div class="table-cell">{{ passenger.surname }}</div>
                     <div class="table-cell">{{ passenger.birthdate }}</div>
                  </div>
               </div>
            </div>

            <!-- Editable view for new passengers -->
            <div v-else class="passenger-list">
               <div v-for="(passenger, index) in passengers" :key="index" class="passenger-card">
                  <div class="passenger-header">
                     <span class="passenger-number">{{ $t("taskLogs_passenger") }} {{ index + 1 }}</span>
                     <span class="passenger-type-badge">{{ passenger.type | EFltPaxType }}</span>
                  </div>
                  <div class="passenger-inputs">
                     <div class="input-group">
                        <UISelectBox :options="GenderList" v-model="passenger.gender" lighter />
                     </div>
                     <div class="input-group">
                        <UIInput v-model="passenger.name" :placeholder="$t('taskLogs_firstName')" lighter @input="sanitizeName(passenger)" />
                     </div>
                     <div class="input-group">
                        <UIInput v-model="passenger.surname" :placeholder="$t('taskLogs_lastName')" lighter @input="sanitizeSurname(passenger)" />
                     </div>
                     <div class="input-group">
                        <UIDatePicker v-model="passenger.birthdate" :placeholder="$t('taskLogs_birthdate')" :min="getDatePickerMin(passenger.type)" :max="getDatePickerMax(passenger.type)" lighter />
                     </div>
                  </div>
               </div>
            </div>
         </div>
         <button v-if="!isPassengersReadOnly" class="save-passengers-btn" @click="savePassengers">{{ $t("taskLogs_savePassengers") }}</button>
      </div>

      <!-- ── Main Content: Timeline + Chat ── -->
      <div v-if="activeContentTab === 'chat' || (!task?.groupPnrDetails?.paxEntryEnabled && !hasExistingPassengers)" class="main-content">
         <!-- Timeline -->
         <div class="timeline">
            <div class="timeline-line"></div>
            <div v-for="(step, i) in timelineSteps" :key="i" class="timeline-step">
               <div class="step-indicator" :class="'step-indicator--' + step.state">
                  <svg v-if="step.state === 'done'" class="check-svg" viewBox="0 0 10 10" fill="none">
                     <path d="M2 5L4 7L8 3" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  <span v-else-if="step.state === 'active'" class="active-dot"></span>
               </div>
               <div class="step-info">
                  <p class="step-label" :class="'step-label--' + step.state">{{ step.label }}</p>
                  <p v-if="step.date" class="step-date">{{ step.date }}</p>
               </div>
            </div>
         </div>

         <!-- Chat -->
         <div class="chat-area">
            <div class="messages" ref="messagesContainer">
               <div v-if="loading && logs.length === 0" class="loading-messages">
                  <div v-for="n in 3" :key="n" class="skeleton-row" :class="n % 2 === 0 ? 'skeleton-row--right' : ''">
                     <div class="skeleton skeleton--avatar"></div>
                     <div class="skeleton skeleton--bubble"></div>
                  </div>
               </div>
               <div v-else-if="notes.length === 0 && !loading" class="no-messages">{{ $t("taskLogs_noMessages") }}</div>
               <div v-for="(note, i) in notes" :key="i" class="chat-msg" :class="isMyMessage(note) ? 'chat-msg--right' : 'chat-msg--left'">
                  <div class="chat-msg-content">
                     <div class="avatar" :class="isMyMessage(note) ? 'avatar--agent' : 'avatar--op'">{{ getInitials(note.author) }}</div>
                     <div class="chat-msg-body">
                        <div class="chat-msg-meta">
                           <template v-if="isMyMessage(note)">
                              <span class="message-time">{{ note.insertedDatetime | FormatterDateTime }}</span>
                              <span class="sender-name">{{ note.author || $t("taskLogs_operator") }}</span>
                           </template>
                           <template v-else>
                              <span class="sender-name">{{ note.author || $t("taskLogs_operator") }}</span>
                              <span class="message-time">{{ note.insertedDatetime | FormatterDateTime }}</span>
                           </template>
                        </div>
                        <div class="bubble" :class="isMyMessage(note) ? 'bubble--agent' : 'bubble--op'">
                           {{ note.note }}
                        </div>
                     </div>
                  </div>
               </div>
            </div>

            <div class="message-input-row">
               <textarea v-model="newMessage" :placeholder="$t('taskLogs_messagePlaceholder')" class="input-area" rows="2"></textarea>
               <button class="send-btn" @click="sendMessage">{{ $t("taskLogs_sendButton") }}</button>
            </div>
         </div>
      </div>
   </div>
</template>

<script>
import { listTaskLogs, setPnrGroupPaxes } from "@/service/task.api.js";
import { getTask } from "@/service/tasklist.api.js";
import { listNotes, addNote } from "@/service/notes.api.js";
import UISelectBox from "@/components/unitary/form2/UISelectBox.vue";
import UIInput from "@/components/unitary/form2/UIInput.vue";
import UIDatePicker from "@/components/unitary/form2/UIDatePicker2.vue";
import Popup from "@/gts-popup.js";
import { getTaskStatusLabel } from "@/composables/useTaskStatus.js";
import { usePassengers } from "@/composables/usePassengers.js";
import { useRecentTasks } from "@/composables/useRecentTasks.js";
import moment from "moment";

export default {
   name: "TaskLogs",
   components: {
      UISelectBox,
      UIInput,
      UIDatePicker,
   },
   data() {
      return {
         taskId: null,
         task: null,
         logs: [],
         notes: [],
         loading: false,
         error: null,
         newMessage: "",
         passengers: [],
         activeContentTab: "chat",
      };
   },
   computed: {
      GenderList() {
         return [
            { name: this.$i18n.t("common_male"), value: 1 },
            { name: this.$i18n.t("common_female"), value: 2 },
         ];
      },
      currentUserId() {
         return this.$store.state.user?.user?.id;
      },

      formattedBudget() {
         const budget = this.task?.groupPnrDetails?.budgetPerPax;
         if (!budget) return "-";

         const num = parseFloat(budget);
         if (isNaN(num)) return "-";

         return `${num.toFixed(2).replace(".", ",")} €`;
      },
      timelineSteps() {
         if (!this.logs?.logs || !Array.isArray(this.logs.logs)) {
            return [];
         }

         // Sort logs by datetime
         const sortedLogs = [...this.logs.logs].sort((a, b) => new Date(a.logDatetime) - new Date(b.logDatetime));

         // Status label mapping
         const statusLabels = {
            1: this.$t("taskLogs_stepNew"),
            2: this.$t("taskLogs_stepProcessing"),
            3: this.$t("taskLogs_stepWaitingOnOperator"),
            4: this.$t("taskLogs_stepWaitingOnProvider"),
            5: this.$t("taskLogs_stepWaitingOnAgency"),
            6: this.$t("taskLogs_stepClosed"),
            7: this.$t("taskLogs_stepRepliedByProvider"),
            8: this.$t("taskLogs_stepRepliedByAgency"),
         };

         return sortedLogs.map((log, index) => {
            const isLast = index === sortedLogs.length - 1;
            return {
               label: statusLabels[log.subStatus] || `Status ${log.subStatus}`,
               state: isLast ? "active" : "done",
               date: this.$options.filters.FormatterDateTime(log.logDatetime),
            };
         });
      },
      hasExistingPassengers() {
         return this.task?.groupPnrDetails?.passengers && this.task.groupPnrDetails.passengers.length > 0;
      },
      isPassengersReadOnly() {
         return this.hasExistingPassengers;
      },
      lastFlightDate() {
         if (!this.task?.groupPnrDetails) return null;
         const { returnDate, departureDate } = this.task.groupPnrDetails;
         return returnDate || departureDate;
      },
   },
   mounted() {
      this.taskId = this.$route.params.id;
      this.fetchData();
   },
   updated() {
      this.scrollToBottom();
   },
   methods: {
      statusLabel() {
         return getTaskStatusLabel(this.task?.subStatus, (key) => this.$i18n.t(key));
      },
      sanitizeName(passenger) {
         const { sanitizeName } = usePassengers();
         sanitizeName(passenger);
      },
      sanitizeSurname(passenger) {
         const { sanitizeSurname } = usePassengers();
         sanitizeSurname(passenger);
      },
      getDatePickerMin(paxType) {
         if (!this.lastFlightDate) return null;

         switch (paxType) {
            case 2: // child
               return moment(this.lastFlightDate).subtract(12, "years").add(1, "day").format(this.$constants.dateFormat);
            case 3: // infant
               return moment(this.lastFlightDate).subtract(2, "years").add(1, "day").format(this.$constants.dateFormat);
            default: // adult
               return moment(this.lastFlightDate).subtract(100, "years").format(this.$constants.dateFormat);
         }
      },
      getDatePickerMax(paxType) {
         if (!this.lastFlightDate) return null;

         switch (paxType) {
            case 2: // child
               return moment(this.lastFlightDate).subtract(2, "years").subtract(1, "day").format(this.$constants.dateFormat);
            case 3: // infant
               return moment(new Date()).subtract(1, "day").format(this.$constants.dateFormat);
            default: // adult
               return moment(this.lastFlightDate).subtract(12, "years").subtract(1, "day").format(this.$constants.dateFormat);
         }
      },
      async fetchData() {
         this.loading = true;
         this.error = null;
         try {
            const taskId = parseInt(this.$route.query.taskId || this.$route.params.id);
            const orderId = parseInt(this.$route.query.orderId || 0);

            const [taskRes, logsRes, notesRes] = await Promise.all([
               getTask({ taskId: taskId }),
               listTaskLogs({ taskId: taskId }),
               listNotes({
                  dbState: 1,
                  noteId: 0,
                  orderId: orderId,
                  taskId: taskId,
               }),
            ]);
            // getTask may return { task: {...} } or the task directly
            this.task = taskRes?.task ?? taskRes ?? null;
            this.logs = logsRes;
            this.notes = notesRes?.notes ?? [];

            // Save to recent tasks with full details
            if (this.task) {
               const { addToRecentTasks } = useRecentTasks();
               addToRecentTasks(this.task, this.notes, this.$route.fullPath);
            }

            // Initialize passengers if paxEntryEnabled is true or if existing passengers exist
            if (this.task?.groupPnrDetails?.paxEntryEnabled || this.hasExistingPassengers) {
               this.initializePassengersFromTask();
            }

            // Set active tab based on paxEntryEnabled only (not hasExistingPassengers)
            // If paxEntryEnabled is true, show passengers tab by default
            // Otherwise, always show chat tab by default
            if (this.task?.groupPnrDetails?.paxEntryEnabled === true) {
               this.activeContentTab = "passengers";
            } else {
               this.activeContentTab = "chat";
            }
         } catch (err) {
            this.error = true;
         } finally {
            this.loading = false;
         }
      },
      initializePassengersFromTask() {
         // If passengers already exist in API response, use them
         if (this.task.groupPnrDetails.passengers && this.task.groupPnrDetails.passengers.length > 0) {
            this.passengers = this.task.groupPnrDetails.passengers.map((pax) => ({
               type: pax.type,
               name: pax.name,
               surname: pax.surname,
               gender: pax.gender,
               birthdate: pax.birthdate ? this.$options.filters.FormatterDate(pax.birthdate) : "",
            }));
            return;
         }

         // Otherwise create empty passengers based on paxCounts
         const { initializePassengers } = usePassengers();
         this.passengers = initializePassengers(this.task.groupPnrDetails.paxCounts);
      },
      async savePassengers() {
         // Validate all passengers have required fields
         const { validateAllPassengers } = usePassengers();
         const validation = validateAllPassengers(this.passengers);

         if (!validation.isValid) {
            Popup.warning(this.$t("common_warning"), this.$t("taskLogs_fillAllPassengerFields"));
            return;
         }

         try {
            const taskId = parseInt(this.$route.query.taskId || this.$route.params.id);
            const requestModel = {
               taskId: taskId,
               paxes: this.passengers,
            };

            const response = await setPnrGroupPaxes(requestModel);

            if (response?.result?.success) {
               Popup.success(this.$t("common_success"), this.$t("taskLogs_passengersSavedSuccessfully")).confirmed((res) => {
                  if (res.success) {
                     this.$store.commit("app/setIsProgressGifActive", true);
                     setTimeout(() => {
                        this.$store.commit("app/setIsProgressGifActive", false);
                        this.fetchData();
                     }, 1000);
                  }
               });
            } else {
               const errorMessage = response?.result?.subCode || response?.result?.message || this.$t("common_unexpectedError");
               Popup.error(this.$t("common_error"), errorMessage);
            }
         } catch (error) {
            console.error("Error saving passengers:", error);
            Popup.error(this.$t("common_error"), this.$t("common_unexpectedError"));
         }
      },
      isMyMessage(note) {
         return this.currentUserId && note.insertedUserId === this.currentUserId;
      },
      getInitials(fullName) {
         if (!fullName) return "?";
         return fullName
            .split(" ")
            .slice(0, 2)
            .map((w) => w[0])
            .join("")
            .toUpperCase();
      },
      async sendMessage() {
         if (!this.newMessage.trim()) return;

         const taskId = parseInt(this.$route.query.taskId || this.$route.params.id);

         try {
            await addNote({
               taskId: taskId,
               orderId: 0,
               dbState: "1",
               noteId: "0",
               note: this.newMessage,
            });

            this.newMessage = "";
            await this.fetchData();
         } catch (err) {
            // Handle error silently
         }
      },
      scrollToBottom() {
         this.$nextTick(() => {
            const container = this.$refs.messagesContainer;
            if (container) {
               container.scrollTop = container.scrollHeight;
            }
         });
      },
   },
};
</script>

<style lang="scss" scoped>
/* ── Skeleton ── */
.skeleton {
   background: linear-gradient(90deg, var(--gray-200) 25%, var(--gray-100) 50%, var(--gray-200) 75%);
   background-size: 200% 100%;
   animation: shimmer 1.4s infinite;
   border-radius: 4px;

   &--xs {
      width: 60px;
      height: 10px;
   }
   &--sm {
      width: 100px;
      height: 12px;
   }
   &--badge {
      width: 180px;
      height: 28px;
      border-radius: 999px;
   }
   &--icon {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      flex-shrink: 0;
   }
   &--avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      flex-shrink: 0;
   }
   &--bubble {
      width: 280px;
      height: 48px;
      border-radius: 8px;
   }
}

@keyframes shimmer {
   0% {
      background-position: 200% 0;
   }
   100% {
      background-position: -200% 0;
   }
}

.skeleton-row {
   display: flex;
   align-items: flex-start;
   gap: 12px;

   &--right {
      flex-direction: row-reverse;
   }
}

/* ── Card ── */
.detail-card {
   display: flex;
   flex-direction: column;
   // gap: 24px;
}

/* ── Header ── */
.detail-header {
   display: flex;
   align-items: center;
   justify-content: space-between;
}

.header-left {
   flex: 1;
}

/* ── Info Cards ── */
.info-cards {
   display: flex;
   align-items: center;
   gap: 0;
   padding: 16px 0;
   border: 1px solid var(--border-default);
   border-radius: 8px;
   background-color: var(--surface-level-2);
   margin-bottom: 1.25rem;
}

.info-item {
   flex: 1;
   display: flex;
   flex-direction: column;
   gap: 4px;
   padding: 0 16px;
   min-width: 0;
}

.info-separator {
   width: 1px;
   height: 32px;
   background: var(--border-subtle);
   flex-shrink: 0;
}

.info-label {
   font-size: 12px;
   color: var(--gray-500);
   margin: 0;
   font-weight: 500;
}

.info-value {
   font-size: 14px;
   font-weight: 600;
   color: var(--txt-color);
   margin: 0;
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;

   &.last-ticket-date-time {
      font-weight: 700;
      background-color: var(--orange-100);
      color: var(--orange-600);
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      white-space: nowrap;
   }
}

.error-state {
   font-size: 14px;
   color: var(--red-600);
   text-align: center;
   padding: 8px 0;
}

/* ── Timeline Mobile ── */
.timeline-mobile {
   display: flex;
   flex-direction: column;
   gap: 16px;
   padding: 16px;
   background: var(--surface-level-2);
   border-radius: 12px;
   position: relative;

   @media (min-width: 992px) {
      display: none;
   }

   .timeline-step {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      position: relative;
      z-index: 1;
   }
}

/* ── Main Content ── */
.main-content {
   display: flex;
   border: 1px solid var(--border-default);
   border-radius: 16px;
   overflow: hidden;
   min-height: 600px;
}

/* ── Timeline ── */
.timeline {
   width: 288px;
   flex-shrink: 0;
   background: var(--surface-level-2);
   padding: 36px 28px;
   display: none;
   flex-direction: column;
   gap: 24px;
   position: relative;

   @media (min-width: 992px) {
      display: flex;
   }
}

.timeline-line {
   position: absolute;
   left: 37px;
   top: 52px;
   width: 2px;
   height: calc(100% - 100px);
   background: var(--border-default);
}

.timeline-step {
   display: flex;
   align-items: flex-start;
   gap: 12px;
   position: relative;
   z-index: 1;
}

.step-indicator {
   width: 20px;
   height: 20px;
   border-radius: 50%;
   flex-shrink: 0;
   display: flex;
   align-items: center;
   justify-content: center;

   &--done {
      background: var(--green-600);
   }
   &--active {
      background: var(--surface-level-1);
      border: 2px solid var(--blue-600);
   }
   &--pending {
      background: var(--surface-level-1);
      border: 2px solid var(--border-default);
   }
}

.check-svg {
   width: 10px;
   height: 10px;
}

.active-dot {
   width: 8px;
   height: 8px;
   border-radius: 50%;
   background: var(--blue-600);
}

.step-label {
   font-size: 14px;
   font-weight: 500;
   color: var(--gray-600);
   margin: 0;

   &--active {
      font-weight: 600;
      color: var(--blue-600);
   }
   &--pending {
      color: var(--gray-400);
      font-weight: 400;
   }
}

.step-date {
   font-size: 12px;
   color: var(--gray-500);
   margin: 2px 0 0;
}

/* ── Chat ── */
.chat-area {
   flex: 1;
   display: flex;
   flex-direction: column;
   padding: 24px;
   gap: 16px;
   min-height: 0;
   overflow: hidden;
}

.messages {
   flex: 1;
   display: flex;
   flex-direction: column;
   gap: 24px;
   overflow-y: auto;
   min-height: 0;

   &::-webkit-scrollbar {
      width: 6px;
   }

   &::-webkit-scrollbar-track {
      background: var(--surface-level-2);
   }

   &::-webkit-scrollbar-thumb {
      background: var(--gray-300);
      border-radius: 3px;

      &:hover {
         background: var(--gray-400);
      }
   }
}

.loading-messages {
   display: flex;
   flex-direction: column;
   gap: 24px;
}

.no-messages {
   text-align: center;
   color: var(--gray-500);
   font-size: 14px;
   padding: 24px;
}

.chat-msg {
   display: flex;
   align-items: flex-start;
   margin-bottom: 16px;

   &--left {
      justify-content: flex-start;
   }

   &--right {
      justify-content: flex-end;
   }
}

.chat-msg-content {
   display: flex;
   align-items: flex-start;
   gap: 12px;
   max-width: 70%;

   .chat-msg--right & {
      flex-direction: row-reverse;
   }
}

.avatar {
   width: 32px;
   height: 32px;
   border-radius: 50%;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 12px;
   font-weight: 700;
   flex-shrink: 0;

   &--op {
      background: var(--gray-200);
      color: var(--gray-600);
   }
   &--agent {
      background: var(--blue-100);
      color: var(--blue-700);
   }
}

.chat-msg-body {
   display: flex;
   flex-direction: column;
   gap: 6px;
   max-width: 500px;
}

.chat-msg-meta {
   display: flex;
   align-items: center;
   gap: 8px;
}

.sender-name {
   font-size: 12px;
   font-weight: 700;
   color: var(--txt-color);
}

.message-time {
   font-size: 10px;
   color: var(--gray-500);
}

.bubble {
   padding: 12px;
   border-radius: 8px;
   font-size: 14px;
   color: var(--txt-color);
   line-height: 1.5;

   &--op {
      background: var(--surface-level-2);
   }
   &--agent {
      background: var(--blue-0);
      border: 1px solid var(--blue-100);
   }
}

/* ── Input ── */
.message-input-row {
   display: flex;
   align-items: flex-end;
   gap: 16px;
}

.input-area {
   flex: 1;
   border: 1px solid var(--border-default);
   border-radius: 8px;
   padding: 12px;
   font-size: 14px;
   color: var(--txt-color);
   resize: none;
   font-family: inherit;
   min-height: 80px;
   background: var(--surface-level-1);

   &::placeholder {
      color: var(--gray-500);
   }
   &:focus {
      outline: none;
      border-color: var(--accent-primary);
   }
}

.send-btn {
   background: var(--primary);
   color: var(--txt-white);
   border: none;
   border-radius: 8px;
   padding: 12px 24px;
   font-size: 14px;
   font-weight: 600;
   cursor: pointer;
   white-space: nowrap;
   height: 44px;

   &:hover {
      background: var(--blue-700);
   }
}

/* ── Content Tabs ── */
.content-tabs {
   display: flex;
   gap: 0;
   border-bottom: 2px solid var(--border-default);
   margin-bottom: 1.5rem;
}

.tab-button {
   padding: 12px 24px;
   font-size: 14px;
   font-weight: 600;
   color: var(--gray-600);
   background: transparent;
   border: none;
   border-bottom: 3px solid transparent;
   cursor: pointer;
   transition: all 0.2s;

   &:hover {
      color: var(--txt-color);
      background: var(--surface-level-2);
   }

   &.active {
      color: var(--blue-600);
      border-bottom-color: var(--blue-600);
   }
}

/* ── Passenger Entry Section ── */
.passenger-entry-section {
   padding: 1rem 0;
   display: flex;
   flex-direction: column;

   &.table-view {
      padding: 0 !important;
   }
}

.passenger-entry-header {
   padding: 0 0 16px 0;
}

.passenger-entry-header-content {
   flex: 1;
}

.section-title {
   font-size: 18px;
   font-weight: 700;
   color: var(--txt-color);
   margin-bottom: 0 !important;
}

.section-subtitle {
   font-size: 14px;
   color: var(--gray-500);
   margin: 0;
}

.passenger-list {
   display: flex;
   flex-direction: column;
   gap: 0.75rem;
   margin-bottom: 24px;
}

.passenger-card {
   background: var(--surface-level-1);
   border: 1px solid var(--border-default);
   border-radius: 8px;
   padding: 1rem;
}

.passenger-header {
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 0.75rem;
   margin-bottom: 0.5rem;
}

.passenger-number {
   font-size: 14px;
   font-weight: 600;
   color: var(--txt-color);
}

.passenger-type-badge {
   font-size: 11px;
   font-weight: 600;
   padding: 4px 8px;
   border-radius: 4px;
   background: var(--blue-100);
   color: var(--blue-700);
   text-transform: uppercase;
}

.passenger-table {
   border: 1px solid var(--border-default);
   border-radius: 8px;
   overflow: hidden;
   margin-bottom: 24px;

   .passenger-table-header {
      display: grid;
      grid-template-columns: 50px 100px 100px 1fr 1fr 150px;
      background: var(--surface-level-2);
      border-bottom: 2px solid var(--border-default);

      @media (max-width: 768px) {
         display: none;
      }

      .table-header-cell {
         padding: 12px 16px;
         font-size: 12px;
         font-weight: 700;
         color: var(--gray-700);
         text-transform: uppercase;
         letter-spacing: 0.5px;
         border-right: 1px solid var(--border-subtle);

         &:last-child {
            border-right: none;
         }
      }
   }

   .passenger-table-body {
      .passenger-table-row {
         display: grid;
         grid-template-columns: 50px 100px 100px 1fr 1fr 150px;
         border-bottom: 1px solid var(--border-subtle);
         transition: background 0.15s;

         @media (max-width: 768px) {
            grid-template-columns: 1fr;
            padding: 12px;
         }

         &:last-child {
            border-bottom: none;
         }

         &:hover {
            background: var(--surface-level-1);
         }

         .table-cell {
            padding: 14px 16px;
            font-size: 14px;
            color: var(--txt-color);
            display: flex;
            align-items: center;
            border-right: 1px solid var(--border-subtle);

            @media (max-width: 768px) {
               border-right: none;
               padding: 4px 0;

               &:before {
                  content: attr(data-label);
                  font-weight: 700;
                  margin-right: 8px;
                  min-width: 100px;
               }
            }

            &:last-child {
               border-right: none;
            }

            &:first-child {
               font-weight: 600;
               color: var(--gray-600);
            }

            .type-badge {
               font-size: 11px;
               font-weight: 600;
               padding: 4px 8px;
               border-radius: 4px;
               background: var(--blue-100);
               color: var(--blue-700);
               text-transform: uppercase;
            }
         }
      }
   }
}

.passenger-inputs {
   display: grid;
   grid-template-columns: 1fr 1fr 1fr 1fr;
   gap: 12px;

   @media (max-width: 768px) {
      grid-template-columns: 1fr 1fr;
   }
}

.input-group {
   display: flex;
   flex-direction: column;
}

.save-passengers-btn {
   background: var(--primary);
   color: var(--txt-white);
   border: none;
   border-radius: 8px;
   padding: 12px 24px;
   font-size: 14px;
   font-weight: 600;
   cursor: pointer;
   transition: background 0.2s;
   align-self: flex-end;

   &:hover {
      background: var(--blue-700);
   }

   &:disabled {
      background: var(--gray-300);
      cursor: not-allowed;
   }
}
</style>
